const CDN = "https://static.prod-images.emergentagent.com/jobs/a99c2969-7ac0-4b5c-adb1-9cb1c6716fac/images";

// Canonical, brand-consistent product family (single design language, no baked-in brand text)
export const IMG = {
  // Authoritative system map is now a hand-authored SVG (see SystemDiagram.jsx); these images are product renders only
  hero: `${CDN}/43541446639cbd42c220b951ebda48f6c3a94997d755d811689c95635cd7e059.jpeg`,
  controller: `${CDN}/d11fdd46ec39d6ecf1b3eb6e150e7555b444e4758ae0596d6f4f9443251389ed.jpeg`,
  dosing: `${CDN}/946c61d8f83ec070e05ea7a15ae2a373401afa8a3c892753b2aea29961aebb59.jpeg`,
  manifold: `${CDN}/ceb359797ccc4665276f1387a6e0a3f149a8c9433a78528c1b0d94d1712aa3d0.jpeg`,
  moduleFamily: `${CDN}/4be3f170ac4e8f8173bef343c3026d2888415734c7292497dc872d5aacd3463a.jpeg`,
  scene: `${CDN}/43541446639cbd42c220b951ebda48f6c3a94997d755d811689c95635cd7e059.jpeg`,
  app: `${CDN}/23b940ac792591a66d2fff33ad1f8787267a04e37001129e22ed51f473fb9ae6.jpeg`,
  explodedController: `${CDN}/76905ee400dcfe6a3a47b0c97f2a356e8f769b46922ea3c81916e72ce9efd80b.jpeg`,
  explodedManifold: `${CDN}/f35ea8c9ce8b7cf2cf2674cf6150c50787e0f8b67f83f08f54aa4f5d1933f341.jpeg`,
  explodedDosing: `${CDN}/f782dd2442091b584c58fa4f9e23d019b40b632d803dee9d6df6b06c520a6373.jpeg`,
  villa: "https://images.unsplash.com/photo-1613977257363-707ba9348227?crop=entropy&cs=srgb&fm=jpg&q=85&w=1600",
  caustics: "https://images.unsplash.com/photo-1562016600-ece13e8ba570?crop=entropy&cs=srgb&fm=jpg&q=85&w=1600",
};

export const BRAND = {
  name: "Aqualux Nexus",
  tagline: "Modular water automation for modern pools.",
  subTaglines: ["Simpler water. Smarter control.", "Future-ready pool care."],
  pitch:
    "A professionally installed smart controller with a no-replumb expansion backbone and plug-and-play upgrade modules — designed to simplify pool ownership, reduce manual balancing, and modernize residential water care.",
};

export const PROBLEMS = [
  "Confusing water chemistry",
  "Recurring manual dosing",
  "Expensive or fragmented automation",
  "Inconsistent pool-store testing",
  "Overcorrection and wasted chemicals",
  "Maintenance-heavy systems that still don't simplify ownership",
];

export const LEGACY = [
  { label: "Chlorination only", note: "Sanitizes, but ignores balance & dosing." },
  { label: "pH dosing only", note: "Single-parameter, no platform path." },
  { label: "Reagent / probe systems", note: "Ongoing maintenance & calibration burden." },
  { label: "Ionizer / mineral", note: "Still requires separate sanitizer support." },
  { label: "Premium closed systems", note: "Consumables + service overhead, locked ecosystems." },
];

export const PILLARS = [
  {
    key: "controller",
    tag: "01 · Dry Core",
    name: "Smart Core Controller",
    img: IMG.controller,
    envelope: "360 × 280 × 110 mm",
    ip: "IP65 → IP66",
    blurb:
      "The permanent smart brain: automation logic, module recognition, connectivity, sensing and safety lockout — so customers never replace the core when they expand.",
    features: [
      "Automation & safety-lockout logic",
      "Module auto-detection over low-voltage bus",
      "Dual-band Wi-Fi + BLE app connectivity",
      "Front status light bar (Good / Adjusting / Attention)",
      "Segregated mains + gasketed electronics zone",
    ],
  },
  {
    key: "manifold",
    tag: "02 · Wet Backbone",
    name: "Expansion Backbone Manifold",
    img: IMG.manifold,
    envelope: "450–520 × 95–120 × 85–100 mm",
    ip: "Full-bore, serviceable",
    blurb:
      "A professionally installed, threaded/union plumbing foundation that keeps the main line full-bore and enables no-replumb expansion for years to come.",
    features: [
      "40 mm & 50 mm metric compatibility",
      "Removable via threaded flange / union",
      "Capped quick-connect module ports",
      "Anti-backflow injection points",
      "Service without cutting glued joints",
    ],
  },
  {
    key: "dosing",
    tag: "03 · Hybrid Module",
    name: "Smart Dosing Module",
    img: IMG.dosing,
    envelope: "180 × 230 × 110 mm",
    ip: "Child-safe access",
    blurb:
      "The first homeowner-facing upgrade: safer, cleaner metered liquid delivery with fail-safe closed dosing and module auto-detection.",
    features: [
      "Metered pH-correction liquid delivery",
      "Anti-backflow / check-valve protection",
      "Fail-safe closed on power loss or fault",
      "Child-resistant, tool-access reservoir bay",
      "Replaceable cartridge / reservoir interface",
    ],
  },
  {
    key: "app",
    tag: "04 · Software",
    name: "App & Software Platform",
    img: IMG.app,
    envelope: "iOS · Android · Web",
    ip: "OTA update path",
    blurb:
      "Makes the system understandable without exposing chemistry complexity — setup wizard, plain-language status, alerts, and installer/service mode.",
    features: [
      "Guided setup & module recognition",
      "Good / Adjusting / Attention Needed status",
      "Low-reservoir & fault alerts",
      "Installer / service mode",
      "Event logging & firmware updates",
    ],
  },
];

export const EXPLODED = {
  controller: {
    img: IMG.explodedController,
    parts: [
      { n: "Front outer shell", cls: "dry", spec: "UV-stabilized ASA polymer" },
      { n: "Fascia + light-bar lens", cls: "dry", spec: "Optical-grade PC diffuser" },
      { n: "IP66 gasket system", cls: "seal", spec: "Compression silicone seal" },
      { n: "Main control PCB", cls: "dry", spec: "Bortex-class MCU, BLE 5.3" },
      { n: "Low-voltage power section", cls: "dry", spec: "Isolated SELV conversion" },
      { n: "Protected mains enclosure", cls: "dry", spec: "Segregated compartment" },
      { n: "Module bus connector board", cls: "seal", spec: "Keyed low-voltage bus" },
      { n: "Rear mounting shell + bracket", cls: "dry", spec: "Wall-mount interface" },
    ],
  },
  manifold: {
    img: IMG.explodedManifold,
    parts: [
      { n: "Manifold body", cls: "wet", spec: "PVC-U / CPVC full-bore" },
      { n: "Threaded union connectors (×2)", cls: "wet", spec: "Serviceable, no re-glue" },
      { n: "40 / 50 mm adapter set", cls: "wet", spec: "Metric compatibility" },
      { n: "Capped module ports", cls: "wet", spec: "Quick-connect future ports" },
      { n: "Check-valve assemblies", cls: "seal", spec: "Anti-backflow protection" },
      { n: "O-ring & seal kit", cls: "seal", spec: "EPDM chemical-rated" },
      { n: "Mounting bracket system", cls: "dry", spec: "Corrosion-resistant" },
    ],
  },
  dosing: {
    img: IMG.explodedDosing,
    parts: [
      { n: "Front / rear shells", cls: "dry", spec: "Outdoor-rated polymer" },
      { n: "Removable reservoir cartridge", cls: "wet", spec: "Replaceable bay" },
      { n: "Child-safe latch door", cls: "dry", spec: "Tool / restricted access" },
      { n: "Metering pump subassembly", cls: "hybrid", spec: "Serviceable peristaltic head" },
      { n: "Tubing path", cls: "wet", spec: "Chemical-compatible" },
      { n: "Anti-backflow check valve", cls: "seal", spec: "No reservoir pressure feedback" },
      { n: "Low-voltage electronics + bus", cls: "dry", spec: "Isolated from wet path" },
      { n: "Leak containment tray", cls: "seal", spec: "Fault containment pocket" },
    ],
  },
};

export const WETDRY = {
  wet: [
    "Expansion backbone manifold",
    "Dosing fluid path",
    "Dosing injection hardware",
    "Future oxidation / media wet modules",
  ],
  dry: [
    "Controller electronics chamber",
    "App / connectivity hardware",
    "Low-voltage module recognition interface",
    "Some sensor expansion variants",
  ],
  hybrid: ["Smart dosing module", "Sensor block variants"],
};

export const ROADMAP = [
  {
    v: "V1",
    status: "In definition",
    title: "Base Platform",
    items: ["Smart core controller", "Backbone manifold", "Smart dosing module", "Mobile / web app"],
  },
  {
    v: "V2",
    status: "Roadmap",
    title: "Ecosystem Expansion",
    items: ["Sensor expansion module", "Clarity / media cartridge", "Oxidation booster", "Spa high-temperature module"],
  },
];

export const MANUFACTURING = [
  { t: "Shared enclosure family", d: "One industrial-design language across modules." },
  { t: "Standardized module shells", d: "Minimized tooling count for cost control." },
  { t: "Universal rail & data bus", d: "One mounting + electrical interface." },
  { t: "Serviceable subassemblies", d: "Replaceable parts for warranty economics." },
  { t: "China OEM/ODM-friendly", d: "Clear architecture for cost-effective scale." },
];

export const DURABILITY = [
  { k: "Enclosure rating", v: "IP65 base · IP66 premium-spec" },
  { k: "Housing", v: "UV-stabilized outdoor polymer / composite" },
  { k: "Hardware", v: "Corrosion-resistant external fasteners" },
  { k: "Warranty", v: "3-year base · 5-year premium target" },
  { k: "Environment", v: "Sun · rain · chlorine vapor · salt-air · thermal cycling" },
];

export const SAFETY = [
  "Australian design intent first",
  "AS/NZS-aligned electrical safety pathway",
  "RCM-oriented certification planning",
  "Low-voltage modular bus wherever possible",
  "Segregated mains area",
  "Child-safe dosing access",
  "Fail-safe closed dosing on power loss or fault",
  "No uncontrolled chemical discharge",
];

export const COMMERCIAL = {
  model: [
    { t: "Starter hardware package", d: "Controller + backbone + dosing + app." },
    { t: "Add-on module upsell", d: "Future modules without replacing the core." },
    { t: "Accessory & service ecosystem", d: "Consumables, cartridges, service plans." },
    { t: "Premium-spec variants", d: "IP66, extended warranty, higher-tier modules." },
  ],
  wedge:
    "Aqualux Nexus does not need to replace the whole industry on day one. It needs to become the most credible homeowner-friendly automation platform in the residential segment.",
};

export const ASK = {
  seeking: ["Investors", "Manufacturing partners", "Product development partners", "Strategic industry stakeholders"],
  objective: "Legal/IP review, strategic technical validation, and early-stage discussions.",
  steps: [
    "Protect IP & file provisional patent if advised",
    "Finalize industrial design & technical architecture",
    "Build a structured prototype program",
    "Validate installation, durability, and cost targets",
  ],
  closing:
    "Aqualux Nexus is designed to make pool ownership simpler, smarter, and more future-ready — without pretending the problem is solved by another single-purpose box.",
};

export const VAULT = {
  cogs: [
    { part: "Controller enclosure + tooling amort.", cost: 38 },
    { part: "Main PCB + MCU + wireless", cost: 46 },
    { part: "Power / mains section", cost: 22 },
    { part: "Backbone manifold (PVC + unions)", cost: 34 },
    { part: "Dosing module + peristaltic head", cost: 41 },
    { part: "Seals, fasteners, harness, packaging", cost: 33 },
  ],
  msrp: 799,
  patents: [
    { claim: "No-replumb threaded backbone manifold", status: "Provisional draft" },
    { claim: "Rail-shared low-voltage module bus", status: "Provisional draft" },
    { claim: "Fail-safe closed dosing on power loss", status: "In review" },
  ],
  certs: ["AS/NZS 3136 pathway", "RCM (planned)", "IP65 / IP66 target", "FCC Class B (planned)"],
};

export const SLIDES = [
  {
    n: 1,
    tag: "Title",
    title: "Aqualux Nexus",
    subtitle: "Modular water automation for modern pools.",
    highlight: "Compact modular pool & spa automation for homeowners.",
    img: IMG.hero,
    points: [
      "Professionally installed smart controller",
      "No-replumb expansion backbone",
      "Plug-and-play upgrade modules",
    ],
  },
  {
    n: 2,
    tag: "The Problem",
    title: "Pool ownership is too complex, reactive, and costly",
    subtitle: "Homeowners are stuck with fragmented, maintenance-heavy systems.",
    highlight: "The market needs a homeowner-friendly automation platform — not more chlorination hardware.",
    img: IMG.caustics,
    points: PROBLEMS,
  },
  {
    n: 3,
    tag: "Market Gap",
    title: "Why existing solutions fall short",
    subtitle: "Every incumbent solves only part of the problem.",
    highlight:
      "There is no compact, outdoor-rated, no-replumb modular platform that lets homeowners start simple and add capability later.",
    img: IMG.caustics,
    points: LEGACY.map((l) => `${l.label} — ${l.note}`),
  },
  {
    n: 4,
    tag: "The Solution",
    title: "A modular automation ecosystem",
    subtitle: "Built around three layers plus software.",
    highlight: "Plug-and-play retrofit with zero pipe cutting after the initial backbone install.",
    img: IMG.scene,
    points: [
      "Smart Core Controller — automation & connectivity hub",
      "Professional Install Backbone — no-replumb future expansion",
      "Plug-and-Play Modules — smart dosing first, more to come",
      "App Platform — plain-language homeowner control",
    ],
  },
  {
    n: 5,
    tag: "Competitive Moat",
    title: "A platform, not a single-purpose device",
    subtitle: "One-time professional install, future consumer-friendly upgrades.",
    highlight: "Keep the main hydraulic line unrestricted; avoid future replumbing entirely.",
    img: IMG.manifold,
    points: [
      "Full-bore main line, no meaningful restriction",
      "Shared rail + data bus across modules",
      "Wet / dry isolation by design",
      "Smarter control logic reduces overcorrection",
    ],
  },
  {
    n: 6,
    tag: "Product Architecture",
    title: "V1 product definition",
    subtitle: "Controller · Backbone Manifold · Smart Dosing · App.",
    highlight: "Credible & commercially realistic — no overpromising on perfect chemistry forever.",
    img: IMG.hero,
    points: [
      "Provides the permanent automation core",
      "Enables no-replumb future expansion",
      "Controlled, safer liquid dosing",
      "Homeowner status, alerts & simplified operation",
    ],
  },
  {
    n: 7,
    tag: "Expansion Path",
    title: "Product family & roadmap",
    subtitle: "V1 base platform → V2 ecosystem.",
    highlight: "Future upsell without replacing the core — LTV expansion beyond the first hardware sale.",
    img: IMG.dosing,
    points: [
      "V2: sensor expansion module",
      "V2: clarity / media cartridge module",
      "V2: oxidation booster module",
      "V2: spa high-temperature module",
    ],
  },
  {
    n: 8,
    tag: "Engineering Lock",
    title: "Manufacturing & durability",
    subtitle: "Outdoor-rated, serviceable, warranty-ready.",
    highlight: "IP65 base / IP66 premium · 3-year base, 5-year premium warranty target.",
    img: IMG.explodedController,
    points: [
      "UV-stabilized premium outdoor polymer housings",
      "Corrosion-resistant external hardware",
      "Serviceable threaded manifold architecture",
      "China OEM/ODM-friendly shared tooling",
    ],
  },
  {
    n: 9,
    tag: "Business Model",
    title: "Commercial opportunity",
    subtitle: "Hardware sale + recurring ecosystem.",
    highlight: "Starter package → module upsell → accessory/service ecosystem → premium variants.",
    img: IMG.villa,
    points: [
      "Homeowners are frustrated; systems remain fragmented",
      "Recurring chemical overspend is common",
      "Channels lack a modern consumer platform",
      "Strategic wedge: the most credible homeowner platform",
    ],
  },
  {
    n: 10,
    tag: "The Ask",
    title: "Next steps & the ask",
    subtitle: "IP review, technical validation, early-stage discussions.",
    highlight: ASK.closing,
    img: IMG.scene,
    points: ASK.steps,
  },
];
